import { Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { ReduxState } from '../../reducers';
import { TestAccount, UserDetails } from '../../api/users.interface';
import {
    addTestAccount,
    loadTestAccounts,
    removeTestAccount,
    restoreTestAccount,
    searchUserByEmailorUID,
} from '../../actions/users';
import AddTestAccountDialog from './AddTestAccountDialog';
import { ConfirmationDialog } from '../common/Modal';
import {
    buildTestAccountLabel,
    buildTestAccountStateLabel,
} from './testAccounts.utils';

interface ComponentState {
    showAddDialog: boolean;
    removingUid?: string;
    restoringUid?: string;
    saving: boolean;
}

interface ReduxProps {
    loadingTestAccounts?: boolean;
    testAccounts?: TestAccount[];
}

interface ReduxActionProps {
    loadTestAccounts: () => void;
    searchUserByEmailorUID: (query: string) => Promise<UserDetails | undefined>;
    addTestAccount: (testAccount: TestAccount) => Promise<boolean>;
    removeTestAccount: (uid: string) => Promise<boolean>;
    restoreTestAccount: (uid: string) => Promise<boolean>;
}

type Props = ReduxProps & ReduxActionProps;

class TestAccountsPage extends Component<Props, ComponentState> {
    state: ComponentState = {
        showAddDialog: false,
        saving: false,
    };

    componentDidMount() {
        this.props.loadTestAccounts();
    }

    onOpenAddDialog = () => {
        this.setState({ showAddDialog: true });
    }

    onCloseAddDialog = () => {
        this.setState({ showAddDialog: false, saving: false });
    }

    onOpenRemoveDialog = (uid: string) => {
        this.setState({ removingUid: uid });
    }

    onCloseRemoveDialog = () => {
        this.setState({ removingUid: undefined, saving: false });
    }

    onOpenRestoreDialog = (uid: string) => {
        this.setState({ restoringUid: uid });
    }

    onCloseRestoreDialog = () => {
        this.setState({ restoringUid: undefined, saving: false });
    }

    onSearch = async (value: string) => this.props.searchUserByEmailorUID(value);

    onAddTestAccount = async (testAccount: TestAccount) => {
        this.setState({ saving: true });
        const success = await this.props.addTestAccount(testAccount);
        this.setState({ saving: false });
        return success;
    }

    onRemoveTestAccount = async () => {
        const { removingUid } = this.state;
        if (!removingUid) {
            return;
        }

        this.setState({ saving: true });
        const success = await this.props.removeTestAccount(removingUid);
        if (success) {
            this.onCloseRemoveDialog();
            return;
        }

        this.setState({ saving: false });
    }

    onRestoreTestAccount = async () => {
        const { restoringUid } = this.state;
        if (!restoringUid) {
            return;
        }

        this.setState({ saving: true });
        const success = await this.props.restoreTestAccount(restoringUid);
        if (success) {
            this.onCloseRestoreDialog();
            return;
        }

        this.setState({ saving: false });
    }

    render() {
        const { loadingTestAccounts, testAccounts = [] } = this.props;
        const { removingUid, restoringUid, saving, showAddDialog } = this.state;
        const removingAccount = testAccounts.find(
            (testAccount) => testAccount.uid === removingUid,
        );
        const restoringAccount = testAccounts.find(
            (testAccount) => testAccount.uid === restoringUid,
        );

        return (
            <Box sx={{ display: 'grid', gap: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5">Test Accounts</Typography>
                        <Typography variant="body2" color="textSecondary">
                            These are the only clone targets available from a user profile.
                        </Typography>
                    </Box>
                    <Button variant="contained" onClick={this.onOpenAddDialog}>
                        Add Test Account
                    </Button>
                </Box>
                {loadingTestAccounts ? (
                    <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Current Clone</TableCell>
                                    <TableCell>State</TableCell>
                                    <TableCell>UID</TableCell>
                                    <TableCell>Added</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {testAccounts.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <Typography variant="body2" color="textSecondary">
                                                No test accounts have been configured.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {testAccounts.map((testAccount) => (
                                    <TableRow hover key={testAccount.uid}>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <Avatar
                                                    src={testAccount.avatar}
                                                    alt={testAccount.username}
                                                />
                                                <Typography variant="body2">
                                                    {buildTestAccountLabel(testAccount)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{testAccount.email || 'Unknown'}</TableCell>
                                        <TableCell>
                                            {testAccount.currentClonedProfile
                                                ? buildTestAccountLabel(testAccount.currentClonedProfile)
                                                : 'None'}
                                        </TableCell>
                                        <TableCell>{buildTestAccountStateLabel(testAccount)}</TableCell>
                                        <TableCell>{testAccount.uid}</TableCell>
                                        <TableCell>
                                            {testAccount.addedAt
                                                ? new Date(testAccount.addedAt).toLocaleString()
                                                : 'Unknown'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {testAccount.currentClonedProfile && (
                                                <Button
                                                    onClick={() => this.onOpenRestoreDialog(testAccount.uid)}
                                                >
                                                    Restore
                                                </Button>
                                            )}
                                            {!testAccount.currentClonedProfile && (
                                                <Button
                                                    color="error"
                                                    onClick={() => this.onOpenRemoveDialog(testAccount.uid)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <AddTestAccountDialog
                    visible={showAddDialog}
                    submitting={saving}
                    onCancel={this.onCloseAddDialog}
                    onSearch={this.onSearch}
                    onConfirm={this.onAddTestAccount}
                />
                <ConfirmationDialog
                    id="remove-test-account"
                    visible={Boolean(removingUid)}
                    title="Remove Test Account"
                    okButtonText={saving ? 'Removing...' : 'Remove'}
                    onClose={this.onCloseRemoveDialog}
                    onConfirmed={this.onRemoveTestAccount}
                >
                    Remove {removingAccount ? buildTestAccountLabel(removingAccount) : 'this account'} from the test account registry? This does not delete the user account.
                </ConfirmationDialog>
                <ConfirmationDialog
                    id="restore-test-account"
                    visible={Boolean(restoringUid)}
                    title="Restore Test Account"
                    okButtonText={saving ? 'Restoring...' : 'Restore'}
                    onClose={this.onCloseRestoreDialog}
                    onConfirmed={this.onRestoreTestAccount}
                >
                    Restore {restoringAccount ? buildTestAccountLabel(restoringAccount) : 'this account'} to its original profile? This removes the current clone from and brings back the saved test account profile.
                </ConfirmationDialog>
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, {}, ReduxState> = ({
    users,
}: ReduxState) => ({
    loadingTestAccounts: users.loadingTestAccounts,
    testAccounts: users.testAccounts,
});

export default connect<ReduxProps, ReduxActionProps, {}, ReduxState>(
    mapStateToProps,
    { addTestAccount, loadTestAccounts, removeTestAccount, restoreTestAccount, searchUserByEmailorUID },
)(TestAccountsPage);
